import React, { useEffect, useState, useCallback } from "react";
import { PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TEHSILS } from "@/mock/data";
import { PRELIM_Q, docsForQ, FileChip, WardInfo } from "./TehsildarDashboard";

export const TEH_LABEL = Object.fromEntries(TEHSILS.filter((t) => t.value).map((t) => [t.value, t.label]));

function SdoReviewForm({ appId, onDone }) {
  const [app, setApp] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.officerDetail(appId).then((a) => { if (alive) setApp(a); }).catch(() => {});
    return () => { alive = false; };
  }, [appId]);

  if (!app) return <div className="py-10 text-center text-slate-400">लोड होत आहे...</div>;
  const d = app.data || {};
  const pv = app.prelimVerification || {};

  const act = async (action) => {
    if (action === "approve" && !remark.trim()) {
      setErr("अभिप्राय बदलून पात्र करताना शेरा (कारण) आवश्यक आहे."); return;
    }
    setBusy(true); setErr("");
    try {
      await api.sdoReview(appId, { action, remark });
      toast.success(action === "approve"
        ? `अर्ज क्र. ${app.applicationNumber} पात्र करून जिल्हाधिकारी कार्यालयाकडे पाठवला.`
        : `अर्ज क्र. ${app.applicationNumber} ची अपात्रता कायम ठेवली.`);
      onDone();
    } catch (e) { setErr(e.message || "कृती अयशस्वी."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="sdo-review-form">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div>
          <p className="text-[13px] text-slate-500">अर्ज क्रमांक</p>
          <p className="text-[17px] font-bold text-[#1b5e3f]" data-testid="sdo-app-number">{app.applicationNumber}</p>
        </div>
        <div className="text-right">
          <p className="text-[13px] font-semibold text-slate-700">{d.nameMr || app.applicantName}</p>
          <p className="text-[12.5px] text-slate-500">{d.village || d.city}<WardInfo d={d} testId="sdo-ward-info" /> · {TEH_LABEL[app.tehsil] || app.tehsil}</p>
        </div>
      </div>

      {/* तहसीलदार यांचा अभिप्राय व शेरा */}
      <div className="mb-4 rounded-xl bg-red-50 p-3.5 ring-1 ring-red-200" data-testid="tehsildar-opinion-box">
        <p className="text-[13px] font-bold text-red-700">तहसीलदार यांचा अभिप्राय: {pv.decision || app.prelimDecision || "—"}</p>
        {pv.remark && <p className="mt-1 text-[13px] text-slate-700"><span className="font-semibold">शेरा:</span> {pv.remark}</p>}
      </div>

      {/* 6 प्रश्न + तहसीलदार उत्तरे + कागदपत्रे */}
      <div className="space-y-3">
        {PRELIM_Q.map((q, i) => {
          const a = pv[`q${i + 1}`];
          return (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <p className="text-[13.5px] text-slate-700">{i + 1}. {q}</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {docsForQ(i, d).map((doc, k) => <FileChip key={k} file={doc.file} label={doc.label} testId={doc.testId} />)}
              </div>
              <div className="mt-2">
                <span className={`inline-block rounded-full px-3 py-1 text-[12.5px] font-bold ${a === "होय" ? "bg-emerald-100 text-emerald-700" : a === "नाही" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-500"}`}>
                  तहसीलदार उत्तर: {a || "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* SDO कृती */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="mb-2 text-[14px] font-bold text-[#1b5e3f]">उपविभागीय अधिकारी यांची कृती</p>
        <label className="mb-1.5 block text-[13px] font-semibold text-slate-600">शेरा / टीप <span className="text-slate-400">(पात्र करताना आवश्यक)</span></label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} data-testid="sdo-remark"
          placeholder="शेरा / कारण नमूद करा" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
        {err && <p className="mb-2 text-[13px] text-red-600" data-testid="sdo-error">{err}</p>}
        <div className="flex flex-wrap gap-3">
          <button type="button" disabled={busy} onClick={() => act("uphold")} data-testid="sdo-uphold-btn"
            className="rounded-lg bg-red-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
            अपात्रता कायम ठेवा
          </button>
          <button type="button" disabled={busy} onClick={() => act("approve")} data-testid="sdo-approve-btn"
            className="rounded-lg bg-[#5a9e3a] px-5 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
            तहसीलदारांचा अभिप्राय बदलून पात्र करा व जिल्हाधिकारी कार्यालयाकडे पाठवा
          </button>
        </div>
      </div>
    </div>
  );
}

export function SdoReviewQueue({ tehsil }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.officerList("", "", tehsil);
      const pend = (list || []).filter((a) => a.forwardedToSDO)
        .sort((a, b) => new Date(a.submittedAt || a.createdAt || 0) - new Date(b.submittedAt || b.createdAt || 0));
      setQueue(pend);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [tehsil]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-6 text-center text-slate-400">लोड होत आहे...</div>;

  const current = queue[0];

  return (
    <div className="mt-5">
      <p className="mb-2 text-[14px] font-bold text-[#c2570a]">तहसीलदारांकडून अपात्र ठरवून पाठवलेले अर्ज (पडताळणी)</p>
      {!current ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="sdo-all-done">
          <PartyPopper className="mx-auto mb-2 h-7 w-7 text-emerald-600" />
          <p className="text-[14px] font-semibold text-emerald-700">या तहसीलमध्ये पडताळणीसाठी प्रलंबीत अर्ज नाहीत.</p>
        </div>
      ) : (
        <>
          <p className="mb-2 text-[12.5px] text-slate-500">प्रलंबीत: {queue.length} · सर्वात जुना अर्ज आधी दाखवला आहे.</p>
          <SdoReviewForm key={current.id} appId={current.id} onDone={load} />
        </>
      )}
    </div>
  );
}

